/**
 * Moteur du calculateur « combien de likes pour un post LinkedIn » : pur, sans React.
 *
 * Formule : celle de LinkedIn (aide « statistiques de contenu ») : interactions
 * (clics + réactions + commentaires + republications) ÷ impressions × 100.
 * Repères : médianes ContentIn sur 93 696 posts (1 054 comptes, 2023 à août 2026),
 * calculées sur les impressions, telles que reprises dans le guide
 * `taux-engagement-linkedin`. Les seuils « trop peu d'impressions », « dans la
 * norme » (±25 % de la médiane) et « portée faible » (10 % du réseau) sont des
 * repères éditoriaux NeverZero, pas des chiffres publiés : l'outil le dit.
 */

export type PostFormat = 'text' | 'image' | 'video' | 'document';

export const FORMATS: Record<PostFormat, { label: string; medianRate: number; medianInteractions: number }> = {
  text: { label: 'Texte seul', medianRate: 1.54, medianInteractions: 5 },
  image: { label: 'Image', medianRate: 2.94, medianInteractions: 18 },
  video: { label: 'Vidéo', medianRate: 2.78, medianInteractions: 16 },
  document: { label: 'Carrousel (document)', medianRate: 2.67, medianInteractions: 16 },
};

/** Médiane globale du corpus, utilisée quand aucun format n'est connu. */
export const OVERALL_MEDIAN_RATE = 2.3;

/** Médianes par palier d'abonnés (borne basse incluse). */
const TIERS = [
  { min: 0, label: 'moins de 1 000 abonnés', medianRate: 2.17 },
  { min: 1000, label: '1 000 à 2 499 abonnés', medianRate: 2.19 },
  { min: 2500, label: '2 500 à 4 999 abonnés', medianRate: 2.42 },
  { min: 5000, label: '5 000 à 9 999 abonnés', medianRate: 2.24 },
  { min: 10000, label: '10 000 abonnés et plus', medianRate: 1.77 },
];

/** Sous ce nombre d'impressions, un taux ne veut rien dire (repère NeverZero). */
export const MIN_IMPRESSIONS = 200;
/** Un taux est « nettement au-dessus du lot » à partir de 4 % (guide). */
export const TOP_RATE = 4;
const NORM_BAND = 0.25;
/** Part du réseau atteinte en dessous de laquelle on soupçonne un problème de diffusion. */
const LOW_REACH = 0.1;

export interface Inputs {
  format: PostFormat | null;
  followers: string;
  impressions: string;
  reactions: string;
  comments: string;
  reposts: string;
  clicks: string;
}

export type Level = 'incomplete' | 'invalid' | 'no-impressions' | 'too-low' | 'below' | 'normal' | 'above' | 'top';

export interface Result {
  level: Level;
  headline: string;
  summary: string;
  nextStep: string | null;
  caveats: string[];
  interactions: number;
  rate: number | null;
  referenceRate: number | null;
  referenceLabel: string | null;
  tierMedian: { label: string; rate: number } | null;
  reachShare: number | null;
}

/** Entier positif ou nul ; les espaces (y compris insécables) sont tolérés. null = vide, NaN = invalide. */
export function parseCount(raw: string): number | null {
  const cleaned = raw.replace(/\s/g, '');
  if (cleaned === '') return null;
  if (!/^\d+$/.test(cleaned)) return Number.NaN;
  const value = Number(cleaned);
  return Number.isSafeInteger(value) ? value : Number.NaN;
}

export function formatPercent(value: number): string {
  return `${value.toFixed(2).replace('.', ',')} %`;
}

function tierFor(followers: number) {
  let tier = TIERS[0];
  for (const candidate of TIERS) if (followers >= candidate.min) tier = candidate;
  return tier;
}

const EMPTY: Omit<Result, 'level' | 'headline' | 'summary'> = {
  nextStep: null,
  caveats: [],
  interactions: 0,
  rate: null,
  referenceRate: null,
  referenceLabel: null,
  tierMedian: null,
  reachShare: null,
};

export function evaluate(input: Inputs): Result {
  const values = {
    followers: parseCount(input.followers),
    impressions: parseCount(input.impressions),
    reactions: parseCount(input.reactions),
    comments: parseCount(input.comments),
    reposts: parseCount(input.reposts),
    clicks: parseCount(input.clicks),
  };

  if (Object.values(values).some((v) => v !== null && Number.isNaN(v))) {
    return {
      ...EMPTY,
      level: 'invalid',
      headline: 'Un des champs n’est pas un nombre entier',
      summary: 'Saisissez uniquement des chiffres (les espaces entre milliers sont acceptés), sans virgule ni signe.',
    };
  }

  const n = (v: number | null) => v ?? 0;
  const interactions = n(values.reactions) + n(values.comments) + n(values.reposts) + n(values.clicks);
  const anyEntered = [values.reactions, values.comments, values.reposts, values.clicks].some((v) => v !== null);

  if (!anyEntered) {
    return {
      ...EMPTY,
      level: 'incomplete',
      headline: 'Saisissez au moins vos réactions',
      summary: 'Le résultat apparaît dès que vous avez renseigné les réactions de votre post.',
    };
  }

  const format = input.format ? FORMATS[input.format] : null;
  const tier = values.followers && values.followers > 0 ? tierFor(values.followers) : null;
  const tierMedian = tier ? { label: tier.label, rate: tier.medianRate } : null;
  const referenceRate = format ? format.medianRate : OVERALL_MEDIAN_RATE;
  const referenceLabel = format ? `la médiane des posts « ${format.label.toLowerCase()} »` : 'la médiane tous formats';
  const reachShare =
    values.impressions && values.followers && values.followers > 0 ? values.impressions / values.followers : null;
  const base = { ...EMPTY, interactions, referenceRate, referenceLabel, tierMedian, reachShare };

  // Sans impressions : on ne peut pas calculer de taux, on situe le volume d'interactions.
  if (!values.impressions) {
    if (!format) {
      return {
        ...base,
        level: 'no-impressions',
        headline: `${interactions} interaction${interactions > 1 ? 's' : ''}, mais sans impressions on ne peut pas juger`,
        summary:
          'Un nombre de likes ne veut rien dire seul : 5 réactions sur 200 vues et 5 réactions sur 5 000 vues n’ont pas le même sens. Relevez les impressions dans les statistiques du post (sous le post, « Voir les statistiques »), ou choisissez le format pour comparer le volume.',
        nextStep: null,
      };
    }
    const median = format.medianInteractions;
    const ratio = interactions / median;
    const where =
      ratio < 1 - NORM_BAND ? 'en dessous' : ratio > 1 + NORM_BAND ? 'au-dessus' : 'au niveau';
    return {
      ...base,
      level: 'no-impressions',
      headline: `${interactions} interaction${interactions > 1 ? 's' : ''} : ${where} de la médiane des posts « ${format.label.toLowerCase()} »`,
      summary: `La médiane est de ${median} interactions (clics, réactions, commentaires et republications) pour ce format. Ce volume dépend de la taille du réseau et de la portée : pour savoir si votre post est bon ou non, ajoutez ses impressions et l’outil calcule le taux.`,
      caveats:
        values.clicks === null
          ? ['La médiane inclut les clics : si vous ne les avez pas saisis, votre total est sous-estimé.']
          : [],
    };
  }

  const impressions = values.impressions;
  const rate = (interactions / impressions) * 100;
  const caveats: string[] = [];
  if (values.clicks === null) {
    caveats.push(
      'Sans les clics, le taux est sous-estimé : LinkedIn les compte dans les interactions. Ajoutez-les pour comparer à égalité avec les médianes.',
    );
  }
  if (!format) caveats.push('Sans le format du post, la comparaison se fait à la médiane tous formats (2,3 %).');
  const withRate = { ...base, rate, caveats };

  if (interactions > impressions) {
    return {
      ...withRate,
      level: 'invalid',
      headline: 'Plus d’interactions que d’impressions : vérifiez la saisie',
      summary:
        'Un post ne peut pas recevoir plus d’interactions que d’affichages. Vérifiez que vous avez bien saisi les impressions, pas les membres atteints ou les abonnés.',
    };
  }

  if (impressions < MIN_IMPRESSIONS) {
    return {
      ...withRate,
      level: 'too-low',
      headline: `Taux de ${formatPercent(rate)}, mais ${impressions} impressions : trop peu pour juger`,
      summary: `En dessous de ${MIN_IMPRESSIONS} impressions, une seule réaction fait varier le taux de plusieurs points : le chiffre ne dit rien de la qualité du post. Le vrai sujet est la diffusion, pas l’engagement.`,
      nextStep:
        'Regardez ce qui s’est passé dans l’heure suivant la publication : c’est là que LinkedIn décide de montrer ou non le post plus loin.',
    };
  }

  const ratio = rate / referenceRate;
  let level: Level;
  if (rate >= TOP_RATE) level = 'top';
  else if (ratio > 1 + NORM_BAND) level = 'above';
  else if (ratio < 1 - NORM_BAND) level = 'below';
  else level = 'normal';

  const lowReach = reachShare !== null && reachShare < LOW_REACH;
  if (reachShare !== null) {
    caveats.push(
      `Votre post a été affiché ${Math.round(reachShare * 100)} % du nombre de vos abonnés. LinkedIn ne publie pas de repère pour ce ratio : lisez-le avec le taux, jamais seul.`,
    );
  }
  if ((level === 'above' || level === 'top') && impressions < 1000) {
    caveats.push(
      'Avec moins de 1 000 impressions, un bon taux signale souvent un post resté dans le premier cercle (vos contacts les plus actifs), pas un post qui a circulé.',
    );
  }

  const tierSentence = tierMedian
    ? ` Pour votre palier (${tierMedian.label}), la médiane est de ${formatPercent(tierMedian.rate)}.`
    : '';
  const compare = `Votre taux est de ${formatPercent(rate)} pour ${interactions} interaction${interactions > 1 ? 's' : ''} sur ${impressions} impressions ; ${referenceLabel} est de ${formatPercent(referenceRate)}.${tierSentence}`;

  const verdicts: Record<'below' | 'normal' | 'above' | 'top', { headline: string; next: string }> = {
    below: {
      headline: `Sous la norme : ${formatPercent(rate)} contre ${formatPercent(referenceRate)}`,
      next: lowReach
        ? 'Le post a été peu montré : le premier levier est la diffusion, donc la première heure et l’horaire, plus que le texte.'
        : 'Le post a été vu mais peu de gens ont réagi : travaillez l’accroche (les premières lignes avant « voir plus ») et la raison de réagir.',
    },
    normal: {
      headline: `Dans la norme : ${formatPercent(rate)} contre ${formatPercent(referenceRate)}`,
      next: 'Rien d’anormal : pour faire mieux, il faut surtout élargir la diffusion, donc obtenir plus d’interactions dans la première heure.',
    },
    above: {
      headline: `Au-dessus de la norme : ${formatPercent(rate)} contre ${formatPercent(referenceRate)}`,
      next: 'Bon signe. Vérifiez que les impressions ont suivi : un bon taux sur peu de vues ne prouve pas que le post a circulé.',
    },
    top: {
      headline: `Nettement au-dessus du lot : ${formatPercent(rate)}`,
      next: 'À partir de 4 %, le post fait mieux que la plupart. Vérifiez la portée : l’enjeu est maintenant de le montrer à plus de monde.',
    },
  };

  return { ...withRate, level, headline: verdicts[level].headline, summary: compare, nextStep: verdicts[level].next };
}

/** Texte brut du résultat, pour le bouton de copie. */
export function reportText(result: Result): string {
  const lines = [result.headline, '', result.summary];
  if (result.nextStep) lines.push('', `Et ensuite : ${result.nextStep}`);
  for (const caveat of result.caveats) lines.push(`- ${caveat}`);
  lines.push('', 'Calculé avec https://neverzero.agency/tools/combien-de-likes-post-linkedin');
  return lines.join('\n');
}
